import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Dialog, IconButton, TextField } from "@mui/material";
import ReusableButton from "../../Reusable/PrimaryButton/PrimaryButton";
import WhiteButtonn from '../../Reusable/SecondaryButton/SecondaryButton';
import AudioPlayer from 'react-h5-audio-player';
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckIcon from '@mui/icons-material/Check';
import DiamondIcon from '@mui/icons-material/Diamond';
import { voiceClone } from "../../../services/apiService";
import { showToast } from "../../Toast/CommonToast";
import MicNoneIcon from '@mui/icons-material/MicNone';
import { useNavigate, useParams } from "react-router"
import { useLocation } from "react-router";
import { useVoiceCloneSocket } from "../../../context/VoiceProvider";
import { useCreditPoints } from "../../../services/CreditPointsProvider";
import LoaderWithBackdrop from "../../Loader/loader";
import ErrorPopup from "../ErrorPopup/ErrorPopup";

export interface VoicePopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (data: any) => void;
  setRecording?: any;
  setAudioURL?: any;
  audioURL?: string | null;
  recording?: boolean;
  eventId?: string;
  modelName?: any;
  insertedId?: any;
  formType?: any;
}

const VoicePopup: React.FC<VoicePopupProps> = ({
  open,
  onClose,
  onSubmit,
  setRecording,
  setAudioURL,
  audioURL,
  recording,
  eventId,
  modelName,
  insertedId,
  formType
}) => {
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [hasStartedRecording, setHasStartedRecording] = useState(false);
  const [isAudioReady, setIsAudioReady] = useState(false);
  const locationPath = useLocation()
  const { modelId } = useParams();
  const { reconnectAudioWebSocket } = useVoiceCloneSocket();
  const pathName = /^\/humanpersona\/model\/[^/]+$/.test(locationPath.pathname);
  console.log("pathName", pathName)
  const navigate = useNavigate();
  const { fetchCreditPoints } = useCreditPoints();
  const [isLoading, setIsLoading] = useState(false)

  console.log("insertedId", insertedId, formType, eventId)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const scriptText = 'Hi, I\'m using Showcasa.AI to create this talking photo!\nI just uploaded my picture, added a script, and boom — it\'s talking!\nYou can do it too — bring your photos, characters, or even ideas to life.';
  const audioChunks = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const userId = localStorage.getItem("userId") || "";
    const [errorPopupOpen, setErrorPopupOpen] = useState({
      open: false,
      message: "",
    });
  

  const handleCloneVoice = async () => {
    if (!audioURL) {
      showToast('Please record your voice before cloning.', 'error');
      return;
    }

    if (!isAudioReady) {
      showToast('Please wait for the recording to finish processing.', 'warning');
      return;
    }

    reconnectAudioWebSocket('68ca6e36b9b44cdf285a433c');

    try {
      const formData = new FormData();

      // Convert audio chunks to WebM Blob (keeping original format)
      const audioBlob = new Blob(audioChunks.current, { type: "audio/webm" });
      
      // Verify blob has data
      if (audioBlob.size === 0) {
        showToast('Recording is empty. Please try recording again.', 'error');
        return;
      }

      console.log('Audio blob details:', {
        size: audioBlob.size,
        type: audioBlob.type,
        chunks: audioChunks.current.length
      });

      const fileName = `${modelName}_voice.webm`;

      // ✅ Step 1: Download the file locally for verification
      const downloadUrl = URL.createObjectURL(audioBlob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(downloadUrl); // free up memory

      // ✅ Step 2: Continue with API upload
      formData.append("files", audioBlob, fileName);
      formData.append('userId', userId);
      formData.append('eventId', '68ca6e36b9b44cdf285a433c');
      formData.append('name', modelName);
      formData.append('personaModelId', modelId || insertedId);

      console.log('Sending to API - Blob size:', audioBlob.size, 'bytes');

      setIsLoading(true);

      // Send to backend for cloning
      const responseClone = await voiceClone(formData);
      console.log("responseClone", responseClone);
      onClose();
      if (responseClone) {
        await fetchCreditPoints(userId);
        showToast(responseClone?.message, 'success');

        setIsLoading(false);
        if(!pathName){
          navigate(
          `${formType === "Person" || formType === 'addModel' ? '/humanpersona' : '/aipersona'}/model/${insertedId}`
        );
        }
      } else {
        setIsLoading(false);
        console.log('Voice clone response:', responseClone);
      }

    } catch (error: any) {
      setIsLoading(false);
      console.log('Error cloning voice:', error);
      // Check for 401/403 error
      const status = error?.response?.status || error?.status;
      if (status === 401 || status === 403) {
        setErrorPopupOpen({
          open: true,
          message: status === 401
            ? error?.message
            : error?.message
        });
        return;
      }
      showToast(error?.message || 'Error cloning voice', 'error');
      console.error('Error cloning voice:', error);
    }
  };

  // Generate random waveform data for visual effect
  useEffect(() => {
    const generateWaveform = () => {
      // Generate visually consistent heights (min 20, max 100, rounded)
      const data = Array.from({ length: 60 }, () => Math.max(20, Math.round(Math.random() * 80 + 20)));
      setWaveformData(data);
    };
    if (recording && !isPaused) {
      const interval = setInterval(generateWaveform, 100);
      return () => clearInterval(interval);
    }
  }, [recording, isPaused]);

  // Timer for recording
  useEffect(() => {
    if (recording && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [recording, isPaused]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartRecording = async () => {
    setRecording(true);
    setHasStartedRecording(true);
    setIsAudioReady(false);
    audioChunks.current = [];
    setRecordingTime(0);
    setIsPaused(false);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Use webm format with opus codec (widely supported)
      const options = { mimeType: 'audio/webm' };
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunks.current.push(e.data);
          console.log('Audio chunk received:', e.data.size, 'bytes');
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        console.log('Recording stopped, total chunks:', audioChunks.current.length);
        const audioBlob = new Blob(audioChunks.current, { type: "audio/webm" });
        console.log('Final blob size:', audioBlob.size, 'bytes');
        
        if (audioBlob.size > 0) {
          const audioUrl = URL.createObjectURL(audioBlob);
          setAudioURL(audioUrl);
          setIsAudioReady(true);
        } else {
          showToast('Recording failed. Please try again.', 'error');
          setIsAudioReady(false);
        }
      };
      
      // Request data every 1 second to ensure we get chunks
      mediaRecorderRef.current.start(1000);
      console.log('Recording started');
    } catch (error) {
      console.error('Error starting recording:', error);
      showToast('Could not access microphone. Please check permissions.', 'error');
      setRecording(false);
    }
  };

  const handleStopRecording = () => {
    setRecording(false);
    setIsPaused(false);
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      console.log('Stopping recording...');
      mediaRecorderRef.current.stop();
      
      // Stop all tracks to release microphone
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
  };

  const togglePauseResume = () => {
    if (mediaRecorderRef.current) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
      } else {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
      }
    }
  };

  const restartRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    setAudioURL(null);
    setIsAudioReady(false);
    audioChunks.current = [];
    setTimeout(() => {
      handleStartRecording();
    }, 100);
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  useEffect(() => {
    if (audioURL && isAudioReady) {
      audioRef.current = new Audio(audioURL);
      audioRef.current.onended = () => setIsPlaying(false);
    }
  }, [audioURL, isAudioReady]);

  // Reset state when popup closes
  useEffect(() => {
    if (!open) {
      setRecording(false);
      setIsPaused(false);
      setRecordingTime(0);
      setIsPlaying(false);
      setWaveformData([]);
      setHasStartedRecording(false);
      setAudioURL(null);
      setIsAudioReady(false);
      audioChunks.current = [];
      
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    }
  }, [open]);

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#3d4466',
          borderRadius: 3,
          maxWidth: 900,
        }
      }}
    >
 {(errorPopupOpen.open && (errorPopupOpen.message === "Credits not found" || errorPopupOpen.message === "Insufficient credits")) && (
        <ErrorPopup
          open={errorPopupOpen.open}
          message={errorPopupOpen.message}
          onClose={() => setErrorPopupOpen({ open: false, message: "" })}
        />
      )}
      {isLoading && <LoaderWithBackdrop open={isLoading} />}
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        p: 2,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MicIcon sx={{ color: '#fff' }} />
          <Typography sx={{ color: '#fff', fontSize: 18, fontWeight: 500 }}>
            {`Add voice for your persona ${modelName}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ pt: '20px', paddingLeft: 6, paddingRight: 6, paddingBottom: 6}}>
        <Typography sx={{ color: '#fff', fontSize: '15px', fontWeight: 500, mb: 2}}>
          Please read the text prompt showed below while recording 
        </Typography>
        {/* Script Text */}
        <Box sx={{ mb: 6 }}>
          <TextField 
            multiline
            value={scriptText}
            rows={5}
            variant="standard"
            fullWidth
            InputProps={{
              disableUnderline: true,
              readOnly: true,
              style: {
                color: '#fff',
                fontSize: '18px', 
                fontWeight: 400,
                fontFamily: 'var(--font-family-primary)',
                padding: '20px',
                borderRadius: '8px',
                textAlign: 'center',
              }
            }}
          />
        </Box>

        {/* Waveform Visualization */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          mb: 4,
          justifyContent: 'center'
        }}>
          <IconButton 
            onClick={togglePlayPause}
            disabled={!isAudioReady}
            sx={{ 
              color: '#fff',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
              '&:disabled': { opacity: 0.5 },
              width: 56,
              height: 56
            }}
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>

          {/* Waveform */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.5,
            height: 60,
            flex: 1,
            maxWidth: 400
          }}>
            {waveformData.length > 0 ? waveformData.map((height, index) => (
              <Box
                key={index}
                sx={{
                  width: 4,
                  height: `${height}%`,
                  backgroundColor: index < (recordingTime * 2) ? '#8b5cf6' : '#5a5f7e',
                  borderRadius: 2,
                  transition: 'all 0.1s ease'
                }}
              />
            )) : (
              // Show static waveform when not recording
              Array.from({ length: 60 }).map((_, index) => {
                const staticHeight = Math.max(20, Math.round(Math.random() * 80 + 20));
                return (
                  <Box
                    key={index}
                    sx={{
                      width: 4,
                      height: `${staticHeight}%`,
                      backgroundColor: '#5a5f7e',
                      borderRadius: 2,
                    }}
                  />
                );
              })
            )}
          </Box>

          <Typography sx={{ 
            color: '#fff', 
            fontSize: 18,
            minWidth: 60,
            fontWeight: 500
          }}>
            {formatTime(recordingTime)}
          </Typography>
        </Box>

        {/* Control Buttons */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 3,
          mb: 4
        }}>
          <IconButton
            onClick={restartRecording}
            disabled={!hasStartedRecording}
            sx={{
              width: 70,
              height: 70,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: '#fff',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.15)' },
              '&:disabled': { opacity: 0.3 }
            }}
          >
            <RestartAltIcon sx={{ fontSize: 32 }} />
          </IconButton>

          <IconButton
            onClick={recording ? togglePauseResume : handleStartRecording}
            sx={{
              width: 70,
              height: 70,
              backgroundColor: '#8b5cf6',
              color: '#fff',
              '&:hover': { backgroundColor: '#7c4ee0' }
            }}
          >
            {recording ? (isPaused ? <MicIcon sx={{ fontSize: 40 }} /> : <PauseIcon sx={{ fontSize: 40 }} />) : <MicIcon sx={{ fontSize: 40 }} />}
          </IconButton>

          <IconButton
            disabled={!recording}
            onClick={handleStopRecording}
            sx={{
              width: 70,
              height: 70,
              backgroundColor: '#4ade80',
              color: '#fff',
              '&:hover': { backgroundColor: '#3bc96f' },
              '&:disabled': { opacity: 0.3 }
            }}
          >
            <CheckIcon sx={{ fontSize: 32 }} />
          </IconButton>
        </Box>

        {/* Credits Info */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            justifyContent: 'space-between'
          }}
        >
          <Typography 
            onClick={onClose}
            sx={{ 
              color: '#E2C4FF', 
              fontSize: 16, 
              cursor: 'pointer',
              '&:hover': { color: '#E2C4FF' },
              textDecoration: 'underline'
            }}
          >
            Skip this step →
          </Typography>
     
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'center',
                backgroundColor: '#3B3B69',
                padding: '10px',
                borderRadius: '8px'
              }}
            >
              <DiamondIcon sx={{ color: '#a8b0d3', fontSize: 18 }} />
              <Typography sx={{ 
                color: '#a8b0d3', 
                fontSize: 14,
                cursor: 'pointer',
                '&:hover': { color: '#fff' },
              }}>
                450 credits required to clone your voice
              </Typography>
            </Box>
            <ReusableButton
              icon={<MicNoneIcon style={{ color: '#fff' }} />}
              onClick={handleCloneVoice}
              disabled={!isAudioReady || isLoading}
            >
              Add own voice
            </ReusableButton> 
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default VoicePopup;